import Header from "@/components/ui/Header";
import Category from "@/features/public/Category";
import { RegistrationForm } from "@/features/public/RegistrationForm";
import { SubmittedStatus } from "@/features/public/SubmittedStatus";
import { CheckRecord } from "@/features/public/CheckRecord";
import { useEffect, useState } from "react";
import type {
  CategoryKey,
  CheckRecordResponse,
  RegistrationResponse,
} from "@/features/public/types/public";
import type { RegistrationRequest } from "@/features/public/types/public";
import type { ReapplyPrefill } from "@/features/public/types/public";
import { reapply } from "@/services/public.service";
import { message } from "antd";

type View = "check" | "category" | "form" | "status";

const STORAGE_KEY = "public-registration-flow";

type FlowState = {
  view: View;
  category: CategoryKey | null;
  myKad: string;
  registration: RegistrationResponse | null;
  checkRecord: CheckRecordResponse | null;
  reapplyPrefill: Partial<RegistrationRequest> | null;
};

const defaultFlowState: FlowState = {
  view: "check",
  category: null,
  myKad: "",
  registration: null,
  checkRecord: null,
  reapplyPrefill: null,
};

const readFlowState = (): FlowState => {
  if (typeof window === "undefined") {
    return defaultFlowState;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultFlowState;
    }

    const parsed = JSON.parse(raw) as Partial<FlowState>;
    return {
      view: parsed.view ?? defaultFlowState.view,
      category: parsed.category ?? defaultFlowState.category,
      myKad: parsed.myKad ?? defaultFlowState.myKad,
      registration: parsed.registration ?? defaultFlowState.registration,
      checkRecord: parsed.checkRecord ?? defaultFlowState.checkRecord,
      reapplyPrefill: parsed.reapplyPrefill ?? defaultFlowState.reapplyPrefill,
    };
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return defaultFlowState;
  }
};

const mapReapplyPrefillToForm = (
  prefill: ReapplyPrefill,
): Partial<RegistrationRequest> => {
  if (!prefill) return {};

  const occupationKey =
    prefill.occupationKey ?? (prefill.occupation ? "LAIN-LAIN" : undefined);
  const houseStatusKey =
    prefill.houseStatusKey ??
    (prefill.houseDetails ? "LAIN-LAIN" : undefined);

  return {
    identityNo: prefill.identityNo,
    firstName: prefill.firstName,
    lastName: prefill.lastName,
    mdGenderKey: prefill.genderKey,
    yearOfBirth: prefill.yearOfBirth,
    age: prefill.age,
    phoneNumber: prefill.phoneNumber,
    mdVillageId: prefill.mdVillageId,
    mdResidentDurationKey: prefill.residentDurationKey,
    address1: prefill.address1,
    address2: prefill.address2,
    houseBenchmark: prefill.houseBenchmark,
    mdOccupationKey: occupationKey,
    occupation: prefill.occupation,
    mdHouseStatusKey: houseStatusKey,
    houseDetails: prefill.houseDetails,
    mdFerryFrequencyKey: prefill.ferryFrequencyKey,
    totalMotorcycle: prefill.totalMotorcycle,
    totalCar: prefill.totalCar,
    totalElectricVehicle: prefill.totalElectricVehicle,
    registrationRemark: prefill.registrationRemark,
  };
};

export default function HomePage() {
  const initialState = readFlowState();
  const [view, setView] = useState<View>(initialState.view);
  const [category, setCategory] = useState<CategoryKey | null>(
    initialState.category,
  );
  const [myKad, setMyKad] = useState(initialState.myKad);
  const [registration, setRegistration] = useState<RegistrationResponse | null>(
    initialState.registration,
  );
  const [checkRecord, setCheckRecord] = useState<CheckRecordResponse | null>(
    initialState.checkRecord,
  );
  const [reapplyPrefill, setReapplyPrefill] =
    useState<Partial<RegistrationRequest> | null>(initialState.reapplyPrefill);

  useEffect(() => {
    if (!checkRecord) {
      setReapplyPrefill(null);
    }
  }, [checkRecord]);

  useEffect(() => {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        view,
        category,
        myKad,
        registration,
        checkRecord,
        reapplyPrefill,
      }),
    );
  }, [view, category, myKad, registration, checkRecord, reapplyPrefill]);

  const statusRecord = registration && !checkRecord;

  const handleReapply = async () => {
    if (!checkRecord?.registrationCode) {
      message.error("Kod pendaftaran tidak dijumpai.");
      return;
    }

    try {
      const res = await reapply({
        registrationCode: checkRecord.registrationCode,
        identityNo: myKad,
      });

      if (!res.status || !res.data) {
        message.error("Gagal memproses permohonan semula.");
        return;
      }

      if (!res.data.isReapplyAllowed) {
        message.warning("Permohonan semula tidak dibenarkan.");
        return;
      }

      const prefill = res.data.prefill;
      if (!prefill) {
        message.warning("Data pra-isi tidak tersedia.");
        return;
      }

      const nextCategory = prefill.categoryKey ?? checkRecord.categoryKey;
      if (!nextCategory) {
        message.error("Kategori tidak dijumpai untuk borang semula.");
        return;
      }

      // Clear previous check and registration records before reapplying
      setCheckRecord(null);
      setRegistration(null);

      setCategory(nextCategory);
      setMyKad(
        (prefill.identityNo ?? myKad).replace(/[-\s]+/g, "").toUpperCase(),
      );
      setReapplyPrefill(mapReapplyPrefillToForm(prefill));
      setView("form");
    } catch (error) {
      console.error(error);
      message.error("Permohonan semula gagal. Sila cuba lagi.");
    }
  };

  return (
    <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col justify-center">
      <Header title={view === "form" ? "Pendaftaran Baru" : ""} />

      {view === "check" && (
        <CheckRecord
          onVerified={(clean, nextView, record) => {
            setMyKad(clean);

            if (nextView === "status") {
              setCheckRecord(record);
              setView("status");
              return;
            }

            setView("category");
          }}
        />
      )}

      {view === "category" && (
        <Category
          onBack={() => setView("check")}
          onSelect={(categoryKey) => {
            setCategory(categoryKey);
            setView("form");
          }}
        />
      )}

      {view === "form" && category && (
        <RegistrationForm
          category={category}
          identityNo={myKad}
          prefill={reapplyPrefill ?? undefined}
          onBack={() => setView("category")}
          onSubmit={(data) => {
            setCheckRecord(null);
            setRegistration(data);
            setReapplyPrefill(null);
            setView("status");
          }}
        />
      )}

      {view === "status" && (
        <>
          {checkRecord && (
            <SubmittedStatus
              onBack={() => setView("check")}
              record={checkRecord}
              onReapply={handleReapply}
            />
          )}

          {statusRecord && (
            <SubmittedStatus
              onBack={() => setView("check")}
              record={registration}
            />
          )}
        </>
      )}
    </div>
  );
}
